import type { ReactNode } from 'react'
import './settingsKit.css'

export interface SettingsSectionProps {
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  tone?: 'default' | 'danger'
  flush?: boolean
  children?: ReactNode
  className?: string
  testId?: string
  id?: string
}

/**
 * SettingsSection (SR19, §3.3)
 * White rounded Mosaic panel (--radius-panel, Mosaic surface and border) by default.
 * Optional bold title, description, and actions above it. Rows inside divided by thin lines.
 * tone="danger" applies the danger tone to the panel and title.
 * flush keeps children edge-to-edge (for tables/choice grids).
 */
export function SettingsSection({
  title,
  description,
  actions,
  tone = 'default',
  flush = false,
  children,
  className,
  testId,
  id,
}: SettingsSectionProps) {
  const hasHeader = Boolean(title || description || actions)
  const isDanger = tone === 'danger'

  return (
    <section
      id={id}
      className={[
        'skSection',
        isDanger ? 'skSection--danger' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
    >
      {hasHeader ? (
        <div className="skSection__header">
          <div className="skSection__headerText">
            {title ? <h2 className="skSection__title">{title}</h2> : null}
            {description ? (
              <p className="skSection__description">{description}</p>
            ) : null}
          </div>
          {actions ? (
            <div className="skSection__actions">{actions}</div>
          ) : null}
        </div>
      ) : null}
      <div
        className={[
          'skSection__panel',
          isDanger ? 'skSection__panel--danger' : '',
          flush ? 'skSection__panel--flush' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
    </section>
  )
}
